import React from 'react';
import {
  Button, ButtonColor, ButtonVariant, SpacingName, TypographyName,
} from '@doveit/lego';
import { NextPage } from 'next';
import AppLayout from '../client/layouts/app-layout/AppLayout';
import SectionDivider from '../client/components/section-divider/SectionDivider';
import Faq, { FaqProps } from '../client/templates/common/faq/Faq';
import CitiesSection from '../client/templates/common/cities-section/CitiesSection';
import {
  goToPage, buildAgencyByLocalityURLObject, buildAgenciesURLObject,
} from '../client/utils/links';
import StatsSection, { StatsSectionProps } from '../client/templates/common/stats-section/StatsSection';
import PageSection from '../client/components/page-section/PageSection';
import PageHead, { PageHeadProps } from '../client/components/page-head/PageHead';
import { City, TextAlign } from '../client/types';
import ReviewsSection, { ReviewsSectionProps } from '../client/templates/common/reviews-section/ReviewsSection';
import Intro, { IntroProps } from '../client/components/intro/Intro';
import PageLayout from '../client/layouts/page-layout/PageLayout';
import CreateLeadSection from '../client/templates/common/create-lead-section/CreateLeadSection';
import { sendLeadSubmitEventAndRedirect } from '../client/utils/tracking';
import { getHighlightedCities } from '../services/cities/cities';
import { withError, withGetServerSideError } from '../client/hocs/with-server-side-error/withServerSideError';
import { importDynamicData } from '../services/importDynamicData';

export interface AboutUsProps {
  SEO: PageHeadProps['SEO'],
  intro: IntroProps,
  faq: FaqProps,
  citiesSection: {
    title: string,
    description: string,
  },
  cities: City[],
  reviews: ReviewsSectionProps,
  stats: StatsSectionProps,
  form: {
    title: string,
    intro: string,
    label: string,
  },
}

export const AboutUs: NextPage<AboutUsProps> = ({
  SEO,
  intro,
  faq,
  citiesSection,
  cities,
  reviews,
  stats,
  form,
}) => (
  <AppLayout activePage="about-us">
    <PageHead SEO={SEO} />
    <PageLayout>
      <PageLayout.Header>
        <PageSection>
          <PageSection.Wrapper>
            <Intro
              title={intro.title}
              description={intro.description}
              image={intro.image}
            />
          </PageSection.Wrapper>
        </PageSection>
      </PageLayout.Header>

      <PageLayout.Content>
        <PageLayout.Main>
          <Faq
            title={faq.title}
            items={faq.items}
          />

          <CitiesSection
            title={citiesSection.title}
            cities={cities.map((city) => {
              const urlObject = buildAgencyByLocalityURLObject({ locality: city.locality });

              return {
                ...city,
                anchorTitle: `Agenzia immobiliare ${city.title}`,
                urlObject,
                onClick: () => goToPage(urlObject),
              };
            })}
            cta={(
              <Button
                label="Scopri tutte le città"
                variant={ButtonVariant.OUTLINE}
                color={ButtonColor.PRIMARY}
                href={buildAgenciesURLObject().as}
                onClick={() => goToPage(buildAgenciesURLObject())}
              />
            )}
          />

          <SectionDivider />

          <StatsSection
            title={stats.title}
            items={stats.items}
          />

          <ReviewsSection
            title={reviews.title}
            showReviewLink
          />

          <PageSection padding={[SpacingName.BIG, 0, SpacingName.BIGGEST]}>
            <PageSection.Wrapper>
              <PageSection.Title
                content={form.title}
                textAlign={TextAlign.CENTER}
              />
              <PageSection.Description
                text={form.intro}
                textAlign={TextAlign.CENTER}
                size={TypographyName.H4}
                color="neutrals.dark"
              />
              <CreateLeadSection
                title={form.label}
                dataRef="lead-form-main"
                dataTracking="lead-form-main-submit"
                onSuccess={(lead) => sendLeadSubmitEventAndRedirect('main', lead)}
              />
            </PageSection.Wrapper>
          </PageSection>
        </PageLayout.Main>
      </PageLayout.Content>
    </PageLayout>
  </AppLayout>
);

export const getServerSideProps = withGetServerSideError<AboutUsProps>(async () => {
  const [data, common, cities] = await Promise.all([
    importDynamicData('pages/about-us.yml'),
    importDynamicData('common.yml'),
    getHighlightedCities(),
  ]);
  return {
    props: {
      ...data,
      citiesSection: common.citiesSection,
      cities,
      reviews: common.reviews,
    },
  };
});

export default withError(AboutUs);
